import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { ArrowLeft, Globe } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const loggedUser = await login(email, password);
      if (loggedUser?.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/app");
      }
    } catch (err) {
      setErr(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const isInstalledApp = typeof window !== "undefined" && (
    window.matchMedia('(display-mode: standalone)').matches || 
    window.navigator.standalone || 
    document.referrer.includes('android-app://') ||
    navigator.userAgent.includes('HamarsathiApp')
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#121C2A] flex flex-col justify-center py-10 px-4 sm:px-6 lg:px-8 font-sans">
      
      {/* Top Header Controls: Back to Home & Language Toggle */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md flex items-center justify-between mb-6 px-1">
        {!isInstalledApp ? (
          <Link to="/" className="inline-flex items-center text-sm font-semibold text-[#8F000D] hover:underline gap-1">
            <ArrowLeft className="w-4 h-4" /> {language === "hi" ? "होम पेज" : "Home"}
          </Link>
        ) : (
          <div />
        )}
        <button
          onClick={() => setLanguage(language === "en" ? "hi" : "en")}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#8F000D] bg-white border border-[#8F000D]/20 px-3 py-1.5 rounded-full shadow-sm hover:bg-[#8F000D]/5 transition-all ml-auto"
        >
          <Globe className="w-3.5 h-3.5" />
          <span>{language === "en" ? "हिन्दी" : "English"}</span>
        </button>
      </div>

      {/* Main Title */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <h2 className="text-3xl font-bold font-serif text-[#8F000D] tracking-tight">
          {language === "hi" ? "हमरसथी में लॉगिन करें" : "Login to Hamarsathi"}
        </h2>
        <p className="mt-2 text-sm text-[#5A403E]">
          {language === "hi" ? "अपना जीवनसाथी खोजने के लिए जानकारी दर्ज करें" : "Enter your credentials to find your match"}
        </p>
      </div>

      {/* Login Card */}
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-[#E2BEBA]/40 sm:px-10">
          {err && (
            <div className="mb-4 p-3 rounded-lg bg-[#FFDAD6] text-[#BA1A1A] text-sm font-medium">
              {err}
            </div>
          )}

          <form className="space-y-5" onSubmit={submit}>
            <div>
              <label className="block text-xs font-semibold text-[#121C2A] uppercase tracking-wider mb-1">
                {language === "hi" ? "मोबाइल नंबर या ईमेल आईडी *" : "Mobile Number or Email Address *"}
              </label>
              <Input
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={language === "hi" ? "उदा. 9835760000 या email@gmail.com" : "e.g. 9835760000 or name@gmail.com"}
                className="bg-[#FAF9F6] border-[#8E706D]/30 focus:border-[#8F000D] focus:ring-[#8F000D] h-11"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-xs font-semibold text-[#121C2A] uppercase tracking-wider">
                  {language === "hi" ? "पासवर्ड *" : "Password *"}
                </label>
                <Link to="/forgot-password" className="text-xs text-[#8F000D] font-medium hover:underline">
                  {language === "hi" ? "पासवर्ड भूल गए?" : "Forgot password?"}
                </Link>
              </div>
              <PasswordInput
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#FAF9F6] border-[#8E706D]/30 focus:border-[#8F000D] focus:ring-[#8F000D]"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#8F000D] hover:bg-[#70000A] text-white font-bold py-3 h-11 rounded-xl shadow-md transition-all text-base"
            >
              {loading ? (language === "hi" ? "लॉगिन हो रहा है..." : "Logging in...") : (language === "hi" ? "लॉगिन करें" : "Login")}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 text-muted-foreground font-semibold">
                {language === "hi" ? "या इसके साथ जारी रखें" : "Or continue with"}
              </span>
            </div>
          </div>

          {/* Social / Phone Login Options */}
          <div className="space-y-3">
            <Button asChild variant="outline" className="w-full rounded-xl h-11 justify-center border-slate-200 font-semibold text-slate-700 hover:bg-slate-50">
              <Link to="/login/phone">
                {language === "hi" ? "मोबाइल नंबर (OTP) से साइन इन करें" : "Sign in with Phone Number"}
              </Link>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl h-11 flex items-center justify-center gap-2 border-slate-200 font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                const googleClientId = "1024963400258-ka7ghj9i9hcdobbtrlmhsd87r0biqf71.apps.googleusercontent.com";
                const redirectUri = encodeURIComponent("https://hamarsathi.com/user/social-login/callback/google");
                window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid%20email%20profile`;
              }}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#EA4335" d="M12 5.04c1.7 0 3.2.6 4.4 1.8l3.3-3.3C17.7 1.6 15 1 12 1 7.2 1 3.2 3.8 1.4 8l3.9 3c1-3 3.8-6 6.7-6z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.6h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-9z"/><path fill="#FBBC05" d="M5.3 14.8c-.3-.8-.4-1.8-.4-2.8s.1-2 .4-2.8L1.4 6.2C.5 8 .0 10 .0 12c0 2 .5 4 1.4 5.8l3.9-3z"/><path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3.4 0-6.2-2.3-7.2-5.4l-3.9 3C3.2 20.2 7.2 23 12 23z"/></svg>
              {language === "hi" ? "गूगल खाते से लॉगिन करें" : "Continue with Google"}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#5A403E]">
              {language === "hi" ? "नए सदस्य हैं? " : "New here? "}
              <Link to="/register" className="font-bold text-[#8F000D] hover:underline">
                {language === "hi" ? "मुफ्त पंजीकरण करें" : "Register Free"}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
